import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import "./AipHistoryEnhanced.css";
import { formatYyyyDdMm } from "../utils/date";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload || {};
  return (
    <div className="custom-tooltip">
      <div className="tooltip-date">{label}</div>
      {payload.map((p) => {
        const value = point[p.dataKey];
        return (
          <div key={p.dataKey} className="tooltip-item">
            <strong style={{ color: p.stroke }}>{p.name}:</strong>{" "}
            {Math.round(Number(value) || 0)}
          </div>
        );
      })}
    </div>
  );
};

const TrendCard = ({ label, value, delta }) => {
  const deltaClass =
    delta > 0 ? "trend-up" : delta < 0 ? "trend-down" : "trend-neutral";
  const deltaSymbol = delta > 0 ? "▲" : delta < 0 ? "▼" : "–";
  return (
    <div className="trend-card">
      <div className="trend-label">{label}</div>
      <div className="trend-value">
        <span className="trend-number">{Math.round(value || 0)}</span>
        <span className={`trend-percent ${deltaClass}`}>
          {deltaSymbol} {Math.abs(Math.round(delta || 0))}
        </span>
      </div>
    </div>
  );
};

const AipHistoryEnhanced = ({
  history = [],
  isLoading = false,
  selectedRange = "last3m",
  onRangeChange,
}) => {
  const [viewMode, setViewMode] = useState("both"); // chart | table | both
  const [metricToggles, setMetricToggles] = useState({
    overall: true,
    wellBeing: true,
    careTeam: true,
    environment: true,
    // vocalAnxiety: true,
    // vocalDepression: true,
    // vocalAggression: true,
    // vocalWellness: true,
  });

  const sortedHistory = useMemo(
    () =>
      [...history].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    [history]
  );

  const current = sortedHistory[0];
  const previous = sortedHistory[1];

  const fmt = (n) => (Number.isFinite(n) ? Math.round(n) : "--");
  const delta = (curr, prev) =>
    Number.isFinite(curr) && Number.isFinite(prev) ? Math.round(curr - prev) : 0;

  const metrics = [
    { key: "overall", label: "Overall AIP", color: "#EB25EB" },
    { key: "wellBeing", label: "Well-Being", color: "#22c55e" },
    { key: "careTeam", label: "Care Team", color: "#f59e0b" },
    { key: "environment", label: "Environment", color: "#6366f1" },
    // { key: "vocalAnxiety", label: "Vocal Anxiety", color: "#f97316" },
    // { key: "vocalDepression", label: "Vocal Depression", color: "#ef4444" },
    // { key: "vocalAggression", label: "Vocal Aggression", color: "#c2410c" },
    // { key: "vocalWellness", label: "Vocal Wellness", color: "#10b981" },
  ];

  const activeMetrics = metrics.filter((m) => metricToggles[m.key]);

  return (
    <div className="aip-history-enhanced">
      <div className="history-controls">
        <div className="view-mode-toggle">
          {["chart", "table", "both"].map((mode) => (
            <button
              key={mode}
              className={viewMode === mode ? "active" : ""}
              onClick={() => setViewMode(mode)}
            >
              {mode === "chart" ? "📈 Chart" : mode === "table" ? "📋 Table" : "📊 Both"}
            </button>
          ))}
        </div>
        <div className="history-filters">
          {/* <label>Time Range</label> */}
          <select value={selectedRange} onChange={(e) => onRangeChange?.(e.target.value)}>
            <option value="last7d">Last Week</option>
            <option value="last1m">Last Month</option>
            <option value="last3m">Last 3 Months</option>
            <option value="last6m">Last 6 Months</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="history-loading">
          <div className="spinner" />
          <div>Loading history…</div>
        </div>
      ) : sortedHistory.length === 0 ? (
        <div className="history-empty">
          <div className="empty-icon">📊</div>
          <h3>No AIP history yet</h3>
          <p>When scores are saved, they will appear here.</p>
          <p className="empty-hint">Try saving an assessment to create the first entry.</p>
        </div>
      ) : (
        <>
          <div className="trends-summary">
            <h4>Trends</h4>
            <div className="trend-cards">
              <TrendCard
                label="Overall AIP"
                value={current?.scores?.overall}
                delta={delta(current?.scores?.overall, previous?.scores?.overall)}
              />
              <TrendCard
                label="Well-Being"
                value={current?.scores?.wellBeing}
                delta={delta(current?.scores?.wellBeing, previous?.scores?.wellBeing)}
              />
              <TrendCard
                label="Care Team"
                value={current?.scores?.careTeam}
                delta={delta(current?.scores?.careTeam, previous?.scores?.careTeam)}
              />
              <TrendCard
                label="Environment"
                value={current?.scores?.environment}
                delta={delta(current?.scores?.environment, previous?.scores?.environment)}
              />
            </div>
          </div>

          <div className="statistics-panel">
            <div className="stat-item">
              <div className="stat-label">Total Entries</div>
              <div className="stat-value">{sortedHistory.length}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Latest Overall</div>
              <div className="stat-value">{fmt(current?.scores?.overall)}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Previous Overall</div>
              <div className="stat-value small">{fmt(previous?.scores?.overall)}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Change</div>
              <div
                className={`stat-value small ${
                  delta(current?.scores?.overall, previous?.scores?.overall) > 0
                    ? "stat-up"
                    : delta(current?.scores?.overall, previous?.scores?.overall) < 0
                    ? "stat-down"
                    : ""
                }`}
              >
                {delta(current?.scores?.overall, previous?.scores?.overall)} pts
              </div>
            </div>
          </div>

          <div className="metric-toggles">
            <span className="toggles-label">Metrics:</span>
            {metrics.map((m) => (
              <label key={m.key} className="metric-toggle">
                <input
                  type="checkbox"
                  checked={metricToggles[m.key]}
                  onChange={(e) =>
                    setMetricToggles((prev) => ({
                      ...prev,
                      [m.key]: e.target.checked,
                    }))
                  }
                />
                <span className="metric-indicator" style={{ background: m.color }} />
                {m.label}
              </label>
            ))}
          </div>

          {viewMode !== "table" && sortedHistory.length > 0 && (
            <div className="chart-section">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={sortedHistory
                    .map((h) => ({
                      ...h.scores,
                      date: formatYyyyDdMm(h.timestamp),
                    }))
                    .reverse()}
                  margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  {activeMetrics.map((m) => (
                    <Line
                      key={m.key}
                      type="monotone"
                      dataKey={m.key}
                      stroke={m.color}
                      name={m.label}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {viewMode !== "chart" && (
            <div className="table-section">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>AIP</th>
                    <th>WB</th>
                    <th>CT</th>
                    <th>ES</th>
                    {/* <th>VA</th>
                    <th>VD</th>
                    <th>VG</th>
                    <th>VW</th> */}
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistory.map((h) => (
                    <tr key={h._id || h.timestamp}>
                      <td className="date-cell">
                        {new Date(h.timestamp).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="score-cell">{fmt(h.scores?.overall)}</td>
                      <td className="score-cell">{fmt(h.scores?.wellBeing)}</td>
                      <td className="score-cell">{fmt(h.scores?.careTeam)}</td>
                      <td className="score-cell">{fmt(h.scores?.environment)}</td>
                      {/* <td className="score-cell">{fmt(h.scores?.vocalAnxiety)}</td>
                      <td className="score-cell">{fmt(h.scores?.vocalDepression)}</td>
                      <td className="score-cell">{fmt(h.scores?.vocalAggression)}</td>
                      <td className="score-cell">{fmt(h.scores?.vocalWellness)}</td> */}
                      <td className="reason-cell">{h.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AipHistoryEnhanced;

